
import { RevPicker, Variants } from '@ds/components/Picker'
import isEmpty from 'lodash/isEmpty'

import logger from '@logger'
import { trackClick } from '@tracking/events'

import ColorRound from './ColorRound'
import { PICKER_ICONS } from './Picker.constants'
import translations from './Picker.translations'

export default {
  components: {
    RevPicker,
  },
  props: {
    shouldDisplaySoldOut: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    trackingId: {
      type: String,
      default: '',
    },
    trackingValue: {
      type: String,
      default: '',
    },
    link: {
      type: Object,
      default: null,
    },
    available: {
      type: Boolean,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
    icon: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: null,
    },
    price: {
      type: Object,
      default: () => ({}),
    },
    goodDeal: {
      type: Boolean,
      default: false,
    },
    emphasis: {
      type: String,
      default: '',
    },
    variantForSelectedPickers: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
    }
  },
  computed: {
    disabled() {
      return !this.available
    },
    to() {
      if (!this.available || !this.link) {
        return null
      }

      // Adding a scroll=false param to avoid transitioning when using pickers. See product.vue
      return {
        ...this.link,
        hash: {
          ...this.link.hash,
          scroll: false,
        },
      }
    },
    description() {
      if (isEmpty(this.price) && !this.shouldDisplaySoldOut) {
        return ''
      }

      return this.disabled
        ? this.$t(translations.soldOut)
        : this.$t.price(this.price)
    },
    tag() {
      return this.goodDeal ? this.$t(translations.goodDeal) : ''
    },
    variant() {
      // When a master picker is selected, its variant color is applied to all selected pickers.
      if (this.selected && this.variantForSelectedPickers) {
        return this.variantForSelectedPickers
      }

      // Not selected: let's use the optional emphasis prop, and default to the grey picker.
      return this.emphasis || Variants.Default
    },
    pickerIcon() {
      if (this.icon && Object.keys(PICKER_ICONS).includes(this.icon)) {
        return PICKER_ICONS[this.icon]
      }

      if (this.color) {
        return ColorRound
      }

      // Some colors are missing from the catalog, so we don't display the color round on them. Let's log the info to
      // help the catalog team to fix them!
      if (this.color === '') {
        logger.error('[PRODUCT] Missing color code', {
          colorLabel: this.label,
          productId: this.$route.params.uuid,
        })
      }

      return undefined
    },
    pickerIconProps() {
      if (!this.icon && this.color) {
        return { color: this.color }
      }

      return {}
    },
  },
  watch: {
    $route: async function onRouteChange() {
      this.loading = false
    },
  },
  methods: {
    onClick() {
      if (this.disabled || this.selected) {
        return
      }

      // We're a bit defensive on those trackingId / trackingValue, as the API specs says that
      // both fields are optional. Let's have default values to make sure we're able to detect this
      // in amplitude no matter what
      trackClick({
        zone: this.trackingId ?? 'unknown_picker',
        name: this.trackingValue ?? this.label,
      })

      this.loading = true
      this.$emit('click')
    },
  },
}
